import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase-config';
import { collection, getDocs, onSnapshot, query, orderBy, where, limit, doc, updateDoc, deleteDoc, addDoc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase-config';
import GHLContactManager from '../components/GHLContactManager';
import GHLOpportunityManager from '../components/GHLOpportunityManager';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Import new AI-powered components
import AIAnalyticsDashboard from '../components/AIAnalyticsDashboard';
import MarketingAutomation from '../components/MarketingAutomation';
import PaymentCommissionManager from '../components/PaymentCommissionManager';
import AIAssetUploader from '../components/AIAssetUploader';
import AIAssistant from '../components/AIAssistant';
import HoppXGHLPro from '../components/HoppXGHLPro';
import PartnerManagement from '../components/PartnerManagement';
import ConstructionLeadsManagement from '../components/ConstructionLeadsManagement';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Simple components for dashboard
const MLMTracker = () => (
  <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-[#e31937]/20">
    <h3 className="text-white text-xl font-bold mb-4 flex items-center gap-2">
      <span className="text-2xl">👥</span> MLM Hálózat Követés
    </h3>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-green-500/20 rounded-lg p-4 text-center">
        <div className="text-green-400 text-2xl font-bold">24</div>
        <div className="text-green-300 text-sm">1. Szint</div>
      </div>
      <div className="bg-blue-500/20 rounded-lg p-4 text-center">
        <div className="text-blue-400 text-2xl font-bold">12</div>
        <div className="text-blue-300 text-sm">2. Szint</div>
      </div>
      <div className="bg-purple-500/20 rounded-lg p-4 text-center">
        <div className="text-purple-400 text-2xl font-bold">6</div>
        <div className="text-purple-300 text-sm">3. Szint</div>
      </div>
    </div>
  </div>
);

const CRMLeadManager = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const snapshot = await getDocs(collection(db, "leads"));
        const leadsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setLeads(leadsData);
      } catch (error) {
        console.error("Error fetching leads:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchLeads();
  }, []);

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-[#e31937]/20">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-700 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-[#e31937]/20">
      <h3 className="text-white text-xl font-bold mb-4 flex items-center gap-2">
        <span className="text-2xl">💼</span> CRM Lead Kezelő
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-500/20 rounded-lg p-4 text-center">
          <div className="text-blue-400 text-2xl font-bold">{leads.filter(l => l.status === 'new').length}</div>
          <div className="text-blue-300 text-sm">Új leadek</div>
        </div>
        <div className="bg-yellow-500/20 rounded-lg p-4 text-center">
          <div className="text-yellow-400 text-2xl font-bold">{leads.filter(l => l.status === 'contacted').length}</div>
          <div className="text-yellow-300 text-sm">Kapcsolatfelvétel</div>
        </div>
        <div className="bg-green-500/20 rounded-lg p-4 text-center">
          <div className="text-green-400 text-2xl font-bold">{leads.filter(l => l.status === 'converted').length}</div>
          <div className="text-green-300 text-sm">Konvertált</div>
        </div>
        <div className="bg-purple-500/20 rounded-lg p-4 text-center">
          <div className="text-purple-400 text-2xl font-bold">{leads.length}</div>
          <div className="text-purple-300 text-sm">Összes lead</div>
        </div>
      </div>
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {leads.slice(0, 5).map(lead => (
          <div key={lead.id} className="bg-gray-700/50 rounded-lg p-3 flex justify-between items-center">
            <div>
              <div className="text-white font-medium">{lead.name || 'Névtelen'}</div>
              <div className="text-gray-400 text-sm">{lead.email}</div>
            </div>
            <div className="text-xs px-2 py-1 rounded-full bg-blue-500/20 text-blue-300">
              {lead.status || 'új'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const NewsletterManager = () => {
  const [emailStats, setEmailStats] = useState({
    subscribers: 0,
    totalSent: 0,
    opened: 0,
    clicked: 0,
    openRate: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEmailStats();
  }, []);

  const loadEmailStats = async () => {
    try {
      console.log('🔄 Loading real email statistics...');
      
      // Get newsletter subscribers count from users collection
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const subscribersCount = usersSnapshot.docs.filter(doc => {
        const data = doc.data();
        return data.newsletter === true || data.subscribed === true;
      }).length;
      
      // Try to get email stats from Firestore
      const emailStatsSnapshot = await getDocs(collection(db, 'email_stats'));
      let totalSent = 0, opened = 0, clicked = 0;
      
      emailStatsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        totalSent += data.sent || 0;
        opened += data.opened || 0;
        clicked += data.clicked || 0;
      });
      
      const openRate = totalSent > 0 ? (opened / totalSent * 100) : 0;
      
      setEmailStats({
        subscribers: subscribersCount,
        totalSent,
        opened,
        clicked,
        openRate
      });
      
      console.log('✅ Email stats loaded:', { subscribers: subscribersCount, totalSent, opened, clicked, openRate });
    } catch (error) {
      console.error('❌ Error loading email stats:', error);
      // Fallback to zero stats
      setEmailStats({
        subscribers: 0,
        totalSent: 0,
        opened: 0,
        clicked: 0,
        openRate: 0
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-[#e31937]/20">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e31937]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-[#e31937]/20">
      <h3 className="text-white text-xl font-bold mb-4 flex items-center gap-2">
        <span className="text-2xl">📧</span> Newsletter Kezelő
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-green-500/20 rounded-lg p-4 text-center">
          <div className="text-green-400 text-2xl font-bold">{emailStats.subscribers.toLocaleString()}</div>
          <div className="text-green-300 text-sm">Feliratkozók</div>
        </div>
        <div className="bg-blue-500/20 rounded-lg p-4 text-center">
          <div className="text-blue-400 text-2xl font-bold">{emailStats.totalSent.toLocaleString()}</div>
          <div className="text-blue-300 text-sm">Elküldött email</div>
        </div>
        <div className="bg-yellow-500/20 rounded-lg p-4 text-center">
          <div className="text-yellow-400 text-2xl font-bold">{emailStats.opened.toLocaleString()}</div>
          <div className="text-yellow-300 text-sm">Megnyitva</div>
        </div>
        <div className="bg-purple-500/20 rounded-lg p-4 text-center">
          <div className="text-purple-400 text-2xl font-bold">{emailStats.openRate.toFixed(1)}%</div>
          <div className="text-purple-300 text-sm">Megnyitási arány</div>
        </div>
      </div>
      <button className="w-full bg-[#e31937] hover:bg-[#b91c3c] text-white font-bold py-3 px-6 rounded-lg transition-colors">
        Új kampány indítása
      </button>
    </div>
  );
};

const AdvancedAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState({
    registrations: [],
    pageViews: [],
    conversions: [],
    months: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalyticsData();
  }, []);

  const loadAnalyticsData = async () => {
    try {
      console.log('🔄 Loading real analytics data...');
      const getActivityDashboard = httpsCallable(functions, 'getActivityDashboard');
      const result = await getActivityDashboard();
      
      if (result.data.success) {
        const dashboard = result.data.dashboard;
        
        // Process monthly registrations data
        const monthlyData = dashboard.monthlyStats || {};
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const currentMonth = new Date().getMonth();
        const last6Months = [];
        
        for (let i = 5; i >= 0; i--) {
          const monthIndex = (currentMonth - i + 12) % 12;
          const monthKey = `${new Date().getFullYear()}-${String(monthIndex + 1).padStart(2, '0')}`;
          last6Months.push({
            month: months[monthIndex],
            registrations: monthlyData[monthKey]?.registrations || 0,
            pageViews: monthlyData[monthKey]?.pageViews || 0
          });
        }
        
        setAnalyticsData({
          registrations: last6Months.map(m => m.registrations),
          pageViews: last6Months.map(m => m.pageViews),
          months: last6Months.map(m => m.month)
        });
        
        console.log('✅ Analytics data loaded:', last6Months);
      }
    } catch (error) {
      console.error('❌ Error loading analytics data:', error);
      // Fallback to empty data
      setAnalyticsData({
        registrations: [0, 0, 0, 0, 0, 0],
        pageViews: [0, 0, 0, 0, 0, 0],
        months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']
      });
    } finally {
      setLoading(false);
    }
  };

  const chartData = {
    labels: analyticsData.months,
    datasets: [
      {
        label: 'Regisztrációk',
        data: analyticsData.registrations,
        borderColor: '#e31937',
        backgroundColor: 'rgba(227, 25, 55, 0.1)',
        tension: 0.4,
      },
      {
        label: 'Oldalmegtekintések',
        data: analyticsData.pageViews,
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        labels: {
          color: 'white'
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: 'white'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      },
      y: {
        ticks: {
          color: 'white'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-[#e31937]/20">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e31937]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-[#e31937]/20">
      <h3 className="text-white text-xl font-bold mb-4 flex items-center gap-2">
        <span className="text-2xl">📊</span> Fejlett Analitika
      </h3>
      <div className="h-64">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

const UserActivityDashboard = () => {
  const [activityStats, setActivityStats] = useState({
    todayVisitors: 0,
    weeklyVisitors: 0,
    avgDuration: 0
  });

  useEffect(() => {
    loadActivityStats();
  }, []);

  const loadActivityStats = async () => {
    try {
      console.log('🔄 Loading real activity stats...');
      const getActivityDashboard = httpsCallable(functions, 'getActivityDashboard');
      const result = await getActivityDashboard();
      
      if (result.data.success) {
        const stats = result.data.dashboard;
        setActivityStats({
          todayVisitors: stats.todayStats?.uniqueUsers || 0,
          weeklyVisitors: stats.weeklyStats?.uniqueUsers || 0,
          avgDuration: stats.avgSessionDuration || 0
        });
        console.log('✅ Activity stats loaded:', stats);
      }
    } catch (error) {
      console.error('❌ Error loading activity stats:', error);
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-[#e31937]/20">
      <h3 className="text-white text-xl font-bold mb-4 flex items-center gap-2">
        <span className="text-2xl">👤</span> Felhasználói Aktivitás
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-500/20 rounded-lg p-4 text-center">
          <div className="text-blue-400 text-2xl font-bold">{activityStats.todayVisitors}</div>
          <div className="text-blue-300 text-sm">Mai látogatók</div>
        </div>
        <div className="bg-green-500/20 rounded-lg p-4 text-center">
          <div className="text-green-400 text-2xl font-bold">{activityStats.weeklyVisitors}</div>
          <div className="text-green-300 text-sm">Heti látogatók</div>
        </div>
        <div className="bg-purple-500/20 rounded-lg p-4 text-center">
          <div className="text-purple-400 text-2xl font-bold">{activityStats.avgDuration.toFixed(1)}</div>
          <div className="text-purple-300 text-sm">Átlag. időtartam (perc)</div>
        </div>
      </div>
    </div>
  );
};

const RealTimeNotifications = () => (
  <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-[#e31937]/20">
    <h3 className="text-white text-xl font-bold mb-4 flex items-center gap-2">
      <span className="text-2xl">🔔</span> Valós idejű értesítések
    </h3>
    <div className="space-y-3">
      <div className="bg-green-500/20 rounded-lg p-3 flex items-center gap-3">
        <div className="text-green-400 text-xl">✅</div>
        <div>
          <div className="text-white text-sm font-medium">Új regisztráció</div>
          <div className="text-gray-400 text-xs">Kiss János regisztrált - 2 perce</div>
        </div>
      </div>
      <div className="bg-blue-500/20 rounded-lg p-3 flex items-center gap-3">
        <div className="text-blue-400 text-xl">💬</div>
        <div>
          <div className="text-white text-sm font-medium">Új érdeklődés</div>
          <div className="text-gray-400 text-xs">Nagy Péter érdeklődik - 5 perce</div>
        </div>
      </div>
    </div>
  </div>
);

export default function SuperAdminDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [users, setUsers] = useState([]);
  const [realStats, setRealStats] = useState({
    totalUsers: 0,
    ghlContacts: 0,
    opportunities: 0,
    monthlyRevenue: 0
  });
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [realTimeStats, setRealTimeStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalRevenue: 0,
    conversionRate: 0
  });
  const navigate = useNavigate();

  // Auth check useEffect
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        
        // Check user role from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const role = userData.role || 'user';
            setUserRole(role);
            
            // Only allow super_admin access
            if (role !== 'super_admin') {
              console.log('Access denied. User role:', role);
              setAccessDenied(true);
              setTimeout(() => navigate('/'), 2000);
            }
          } else {
            // Try affiliates collection
            const affiliateDoc = await getDoc(doc(db, 'affiliates', user.uid));
            if (affiliateDoc.exists()) {
              const affiliateData = affiliateDoc.data();
              const role = affiliateData.role || 'affiliate';
              setUserRole(role);
              
              if (role !== 'super_admin') {
                console.log('Access denied. User role:', role);
                setAccessDenied(true);
                setTimeout(() => navigate('/'), 2000);
              }
            } else {
              console.log('Access denied. No user data found.');
              setAccessDenied(true);
              setTimeout(() => navigate('/'), 2000);
            }
          }
        } catch (error) {
          console.error('Error checking user role:', error);
          setAccessDenied(true);
          setTimeout(() => navigate('/'), 2000);
        }
      } else {
        navigate('/');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load real data function
  const loadRealData = async () => {
    try {
      console.log('🔄 Loading real data from Firestore...');
      
      // Load users from Firebase - filter out test users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          joinedAt: doc.data().joinedAt?.toDate() || new Date()
        }))
        .filter(user => {
          // Filter out test/sample users
          const isTestUser = user.email?.includes('test') || 
                            user.email?.includes('sample') || 
                            user.email?.includes('example') ||
                            user.name?.includes('Test') ||
                            user.fullName?.includes('Test') ||
                            user.email?.includes('admin@hopphomes.hu');
          return !isTestUser && user.email && (user.createdAt || user.joinedAt);
        });
      
      console.log(`👥 Found ${usersData.length} real users`);
      setUsers(usersData);

      // Load affiliates collection too
      const affiliatesSnapshot = await getDocs(collection(db, 'affiliates'));
      const affiliatesData = affiliatesSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          joinedAt: doc.data().joinedAt?.toDate() || new Date()
        }))
        .filter(user => {
          const isTestUser = user.email?.includes('test') || 
                            user.email?.includes('sample') || 
                            user.email?.includes('example') ||
                            user.name?.includes('Test') ||
                            user.fullName?.includes('Test') ||
                            user.email?.includes('admin@hopphomes.hu');
          return !isTestUser && user.email && (user.createdAt || user.joinedAt);
        });
      
      console.log(`👥 Found ${affiliatesData.length} real affiliates`);
      
      // Combine users and affiliates, remove duplicates by email
      const allRealUsers = [...usersData];
      affiliatesData.forEach(affiliate => {
        const exists = allRealUsers.find(u => u.email === affiliate.email);
        if (!exists) {
          allRealUsers.push(affiliate);
        }
      });

      // Load GHL contacts count
      const contactsSnapshot = await getDocs(collection(db, 'ghl_contacts'));
      const ghlContacts = contactsSnapshot.size;
      console.log(`📞 Found ${ghlContacts} GHL contacts`);

      // Load opportunities count
      const oppsSnapshot = await getDocs(collection(db, 'ghl_opportunities'));
      const opportunities = oppsSnapshot.size;
      console.log(`💼 Found ${opportunities} opportunities`);

      // Calculate total revenue from opportunities
      const totalRevenue = oppsSnapshot.docs.reduce((sum, doc) => {
        const value = parseFloat(doc.data().value) || 0;
        return sum + value;
      }, 0);
      console.log(`💰 Total revenue: ${totalRevenue} Ft`);

      setRealStats({
        totalUsers: allRealUsers.length,
        ghlContacts,
        opportunities,
        monthlyRevenue: totalRevenue
      });
      
      console.log('✅ Real stats updated:', {
        totalUsers: allRealUsers.length,
        ghlContacts,
        opportunities,
        monthlyRevenue: totalRevenue
      });
      
    } catch (error) {
      console.error('❌ Error loading real data:', error);
      setRealStats({
        totalUsers: 0,
        ghlContacts: 0,
        opportunities: 0,
        monthlyRevenue: 0
      });
    }
  };

  // User management functions using Firebase Functions
  const handleEditUser = async (user) => {
    window.location.href = '/admin/user-management';
  };

  const handleToggleBan = async (user) => {
    const action = user.isBanned ? 'feloldja' : 'tiltja';
    if (confirm(`Biztosan ${action} ezt a felhasználót: ${user.email}?`)) {
      try {
        const updateUserStatus = httpsCallable(functions, 'updateUserStatus');
        await updateUserStatus({
          userId: user.id,
          isActive: user.isBanned // If banned, make active; if not banned, make inactive
        });
        alert(`Felhasználó ${user.isBanned ? 'feloldva' : 'tiltva'}!`);
        // Reload users
        loadAllUsers();
      } catch (error) {
        console.error('Hiba a tiltás során:', error);
        alert('Hiba történt a művelet során!');
      }
    }
  };

  const handleDeleteUser = async (user) => {
    if (confirm(`FIGYELEM! Biztosan véglegesen törli ezt a felhasználót: ${user.email}?\n\nEz a művelet visszavonhatatlan!`)) {
      if (confirm(`Utolsó figyelmeztetés! A felhasználó összes adata véglegesen elvész!\n\nEmail: ${user.email}\nNév: ${user.fullName || 'N/A'}\n\nBiztosan folytatja?`)) {
        try {
          const deleteUser = httpsCallable(functions, 'deleteUser');
          await deleteUser({ userId: user.id });
          alert('Felhasználó sikeresen törölve!');
          // Reload users
          loadAllUsers();
        } catch (error) {
          console.error('Hiba a törlés során:', error);
          alert('Hiba történt a törlés során!');
        }
      }
    }
  };

  // Load all users using Firebase Function
  const loadAllUsers = async () => {
    try {
      console.log('🔍 Loading ALL users from Firebase Function...');
      const getUsersForAdmin = httpsCallable(functions, 'getUsersForAdmin');
      const result = await getUsersForAdmin();
      
      if (result.data.success) {
        console.log(`✅ Loaded ${result.data.users.length} users from Firebase Function`);
        console.log('👥 All users:', result.data.users);
        setUsers(result.data.users);
        
        // Update real stats
        setRealStats(prev => ({
          ...prev,
          totalUsers: result.data.users.length
        }));
      } else {
        console.error('❌ Function returned error:', result.data);
      }
    } catch (error) {
      console.error('❌ Error loading users:', error);
    }
  };

  // Real-time data subscription
  useEffect(() => {
    // Load real data on component mount
    loadRealData();
    
    const unsubscribeUsers = onSnapshot(collection(db, "users"), (snapshot) => {
      // Trigger reload when users collection changes
      loadRealData();
    });

    const unsubscribeLeads = onSnapshot(
      query(collection(db, "leads"), orderBy("createdAt", "desc")), 
      (snapshot) => {
        const leadsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setLeads(leadsData);
      }
    );

    setLoading(false);

    return () => {
      unsubscribeUsers();
      unsubscribeLeads();
    };
  }, []);

  const DashboardTab = () => (
    <div className="space-y-6">
      {/* Real-time Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Moderációra vár"
          value="0"
          icon="⏳"
          color="bg-gradient-to-br from-orange-500 to-orange-600"
          trend="+0%"
        />
        <StatCard
          title="Jóváhagyott"
          value="1"
          icon="✅"
          color="bg-gradient-to-br from-green-500 to-green-600"
          trend="+100%"
        />
        <StatCard
          title="Elutasított"
          value="0"
          icon="❌"
          color="bg-gradient-to-br from-red-500 to-red-600"
          trend="0%"
        />
        <StatCard
          title="Felhasználók"
          value={realTimeStats.totalUsers}
          icon="👥"
          color="bg-gradient-to-br from-blue-500 to-blue-600"
          trend="+12%"
        />
      </div>

      {/* Action Buttons Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <ActionButton icon="📊" label="Moderáció" color="bg-orange-500" onClick={() => setActiveTab("moderation")} />
        <ActionButton icon="💬" label="Autók" color="bg-blue-500" onClick={() => setActiveTab("communication")} />
        <ActionButton icon="👥" label="Felhasználók" color="bg-purple-500" onClick={() => setActiveTab("users")} />
        <ActionButton icon="📈" label="Activity" color="bg-green-500" onClick={() => setActiveTab("analytics")} />
        <ActionButton icon="✅" label="Verifikáció" color="bg-teal-500" onClick={() => setActiveTab("verification")} />
        <ActionButton icon="📧" label="Hírlevel" color="bg-indigo-500" onClick={() => setActiveTab("newsletter")} />
        <ActionButton icon="💬" label="Előzőzések" color="bg-pink-500" onClick={() => setActiveTab("leads")} />
        <ActionButton icon="📊" label="Analitika" color="bg-red-500" onClick={() => setActiveTab("analytics")} />
        <ActionButton icon="🔄" label="AI Pipeline" color="bg-cyan-500" onClick={() => setActiveTab("pipeline")} />
        <ActionButton icon="🎯" label="API" color="bg-red-600" onClick={() => setActiveTab("api")} />
        <ActionButton icon="👁️" label="Figyelés" color="bg-gray-600" onClick={() => setActiveTab("monitoring")} />
        <ActionButton icon="📋" label="CMS" color="bg-purple-600" onClick={() => setActiveTab("cms")} />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#1a1a1a] rounded-xl p-6 border border-[#e31937]/20">
          <h3 className="text-white text-lg font-bold mb-4">Felhasználó Aktivitás</h3>
          <Line
            data={{
              labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
              datasets: [{
                label: 'Aktív Felhasználók',
                data: [12, 19, 3, 5, 2, 3],
                borderColor: '#e31937',
                backgroundColor: 'rgba(227, 25, 55, 0.1)',
                tension: 0.4
              }]
            }}
            options={{
              responsive: true,
              plugins: { legend: { labels: { color: '#fff' } } },
              scales: {
                x: { ticks: { color: '#fff' } },
                y: { ticks: { color: '#fff' } }
              }
            }}
          />
        </div>

        <div className="bg-[#1a1a1a] rounded-xl p-6 border border-[#e31937]/20">
          <h3 className="text-white text-lg font-bold mb-4">Bevétel Megoszlás</h3>
          <Doughnut
            data={{
              labels: ['Affiliate Jutalék', 'Direkt Értékesítés', 'Prémium'],
              datasets: [{
                data: [300, 50, 100],
                backgroundColor: ['#e31937', '#fbbf24', '#22c55e'],
                borderColor: '#1a1a1a',
                borderWidth: 2
              }]
            }}
            options={{
              responsive: true,
              plugins: { legend: { labels: { color: '#fff' } } }
            }}
          />
        </div>
      </div>
    </div>
  );

  const UsersTab = () => {
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");

    useEffect(() => {
      // Filter out mock/dummy users and apply search/role filters
      let filtered = users.filter(user => {
        // Remove mock users (those with dummy emails or names)
        const isDummyUser = 
          user.email?.includes('dummy') ||
          user.email?.includes('test') ||
          user.email?.includes('mock') ||
          user.fullName?.includes('N/A') ||
          user.fullName?.includes('Dummy') ||
          user.fullName?.includes('Test');
        
        return !isDummyUser;
      });

      // Apply search filter
      if (searchTerm) {
        filtered = filtered.filter(user =>
          user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Apply role filter
      if (roleFilter !== "all") {
        filtered = filtered.filter(user => user.role === roleFilter);
      }

      setFilteredUsers(filtered);
    }, [users, searchTerm, roleFilter]);

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white">Felhasználók Kezelése</h2>
          <div className="text-sm text-gray-400">
            Összesen: {filteredUsers.length} valós felhasználó
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Keresés név vagy email alapján..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#2a2a2a] text-white rounded-lg px-4 py-2 border border-gray-600 focus:border-[#e31937] focus:outline-none"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="bg-[#2a2a2a] text-white rounded-lg px-4 py-2 border border-gray-600 focus:border-[#e31937] focus:outline-none"
          >
            <option value="all">Minden szerepkör</option>
            <option value="user">Felhasználó</option>
            <option value="admin">Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>
        
        <div className="bg-[#1a1a1a] rounded-xl overflow-hidden border border-[#e31937]/20">
          <div className="overflow-x-auto">
            <table className="w-full text-white">
              <thead className="bg-[#e31937]">
                <tr>
                  <th className="px-4 py-3 text-left">Név</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Szerepkör</th>
                  <th className="px-4 py-3 text-left">Státusz</th>
                  <th className="px-4 py-3 text-left">Regisztráció</th>
                  <th className="px-4 py-3 text-left">Műveletek</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map(user => (
                    <tr key={user.id} className="border-b border-gray-700 hover:bg-[#2a2a2a]">
                      <td className="px-4 py-3">
                        {user.fullName || user.displayName || user.firstName + ' ' + (user.lastName || '') || 'Névtelen'}
                      </td>
                      <td className="px-4 py-3">{user.email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.role === 'super_admin' ? 'bg-purple-500 text-white' :
                          user.role === 'admin' ? 'bg-red-500 text-white' : 
                          'bg-blue-500 text-white'
                        }`}>
                          {user.role === 'super_admin' ? 'Super Admin' :
                           user.role === 'admin' ? 'Admin' : 
                           'Felhasználó'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.isBanned ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
                        }`}>
                          {user.isBanned ? 'Tiltva' : 'Aktív'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {user.createdAt?.toDate?.()?.toLocaleDateString('hu-HU') || 
                         user.joinedAt?.toDate?.()?.toLocaleDateString('hu-HU') ||
                         new Date(user.createdAt || user.joinedAt).toLocaleDateString('hu-HU') || 
                         'Ismeretlen'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => handleEditUser(user)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
                          >
                            Szerkeszt
                          </button>
                          <button 
                            onClick={() => handleToggleBan(user)}
                            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                              user.isBanned 
                                ? 'bg-green-500 hover:bg-green-600 text-white' 
                                : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                            }`}
                          >
                            {user.isBanned ? 'Felold' : 'Tiltás'}
                          </button>
                          <button 
                            onClick={() => handleDeleteUser(user)}
                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
                          >
                            Töröl
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-400">
                      {searchTerm || roleFilter !== "all" 
                        ? "Nincs találat a szűrési feltételeknek megfelelően."
                        : "Még nincsenek regisztrált felhasználók."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-[#1a1a1a] rounded-lg p-4 border border-gray-700">
            <div className="text-2xl font-bold text-white">{filteredUsers.length}</div>
            <div className="text-gray-400 text-sm">Összes felhasználó</div>
          </div>
          <div className="bg-[#1a1a1a] rounded-lg p-4 border border-gray-700">
            <div className="text-2xl font-bold text-green-400">
              {filteredUsers.filter(u => !u.isBanned).length}
            </div>
            <div className="text-gray-400 text-sm">Aktív felhasználó</div>
          </div>
          <div className="bg-[#1a1a1a] rounded-lg p-4 border border-gray-700">
            <div className="text-2xl font-bold text-red-400">
              {filteredUsers.filter(u => u.isBanned).length}
            </div>
            <div className="text-gray-400 text-sm">Tiltott felhasználó</div>
          </div>
          <div className="bg-[#1a1a1a] rounded-lg p-4 border border-gray-700">
            <div className="text-2xl font-bold text-purple-400">
              {filteredUsers.filter(u => u.role === 'admin' || u.role === 'super_admin').length}
            </div>
            <div className="text-gray-400 text-sm">Admin felhasználó</div>
          </div>
        </div>
      </div>
    );
  };

  const NewsletterTab = () => <NewsletterManager />;

  const MaintenanceTab = () => {
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      loadMaintenanceStatus();
    }, []);

    const loadMaintenanceStatus = async () => {
      try {
        console.log('🔄 Loading maintenance mode status...');
        const maintenanceDoc = await getDocs(query(collection(db, 'system_settings'), where('key', '==', 'maintenance_mode')));
        
        if (!maintenanceDoc.empty) {
          const status = maintenanceDoc.docs[0].data().value;
          setMaintenanceMode(status);
          console.log('✅ Maintenance mode status:', status);
        } else {
          console.log('📝 No maintenance mode setting found, defaulting to false');
          setMaintenanceMode(false);
        }
      } catch (error) {
        console.error('❌ Error loading maintenance status:', error);
        setMaintenanceMode(false);
      } finally {
        setLoading(false);
      }
    };

    const toggleMaintenanceMode = async () => {
      try {
        setLoading(true);
        const newStatus = !maintenanceMode;
        
        console.log(`🔧 ${newStatus ? 'Enabling' : 'Disabling'} maintenance mode...`);
        
        // Update or create maintenance mode setting in Firestore
        const maintenanceDoc = await getDocs(query(collection(db, 'system_settings'), where('key', '==', 'maintenance_mode')));
        
        if (!maintenanceDoc.empty) {
          // Update existing document
          await updateDoc(maintenanceDoc.docs[0].ref, {
            value: newStatus,
            updatedAt: new Date(),
            updatedBy: auth.currentUser?.email
          });
        } else {
          // Create new document
          await addDoc(collection(db, 'system_settings'), {
            key: 'maintenance_mode',
            value: newStatus,
            createdAt: new Date(),
            updatedAt: new Date(),
            updatedBy: auth.currentUser?.email
          });
        }
        
        setMaintenanceMode(newStatus);
        console.log(`✅ Maintenance mode ${newStatus ? 'enabled' : 'disabled'}`);
        
        alert(`Karbantartási mód ${newStatus ? 'bekapcsolva' : 'kikapcsolva'}!`);
      } catch (error) {
        console.error('❌ Error toggling maintenance mode:', error);
        alert('Hiba történt a karbantartási mód váltásakor!');
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="space-y-6">
        <div className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
            <span className="text-2xl">🔧</span>
            Karbantartási Mód Kezelése
          </h3>
          
          <div className="space-y-6">
            {/* Current Status */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-white font-semibold mb-1">Jelenlegi Állapot</h4>
                  <p className="text-gray-400 text-sm">
                    Az oldal jelenleg {maintenanceMode ? 'karbantartási módban' : 'normál üzemmódban'} van
                  </p>
                </div>
                <div className={`px-4 py-2 rounded-full font-semibold ${
                  maintenanceMode 
                    ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                    : 'bg-green-500/20 text-green-400 border border-green-500/30'
                }`}>
                  {maintenanceMode ? '🔴 KARBANTARTÁS' : '🟢 AKTÍV'}
                </div>
              </div>
            </div>

            {/* Toggle Button */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h4 className="text-white font-semibold mb-4">Karbantartási Mód Váltása</h4>
              <div className="space-y-4">
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-yellow-400 text-xl">⚠️</span>
                    <div>
                      <p className="text-yellow-400 font-semibold">Figyelem!</p>
                      <p className="text-gray-300 text-sm mt-1">
                        {maintenanceMode 
                          ? 'A karbantartási mód kikapcsolása után a felhasználók újra hozzáférhetnek az oldalhoz.'
                          : 'A karbantartási mód bekapcsolása után a felhasználók nem férhetnek hozzá az oldalhoz, csak a karbantartási üzenetet látják.'
                        }
                      </p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={toggleMaintenanceMode}
                  disabled={loading}
                  className={`w-full py-4 px-6 rounded-lg font-bold text-white transition-all duration-200 ${
                    loading 
                      ? 'bg-gray-600 cursor-not-allowed'
                      : maintenanceMode
                        ? 'bg-green-600 hover:bg-green-700 shadow-lg hover:shadow-green-500/25'
                        : 'bg-red-600 hover:bg-red-700 shadow-lg hover:shadow-red-500/25'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Frissítés...
                    </div>
                  ) : (
                    <>
                      {maintenanceMode ? '🟢 OLDAL BEKAPCSOLÁSA' : '🔴 KARBANTARTÁSI MÓD BEKAPCSOLÁSA'}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Preview of maintenance message */}
            {maintenanceMode && (
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h4 className="text-white font-semibold mb-4">Karbantartási Üzenet Előnézete</h4>
                <div className="bg-gradient-to-br from-[#e31937] to-[#b91c1c] rounded-lg p-8 text-center">
                  <div className="text-6xl mb-4">🔧</div>
                  <h2 className="text-white text-2xl font-bold mb-4">KARBANTARTÁS ALATT</h2>
                  <p className="text-white/90 text-lg leading-relaxed">
                    Jelenleg karbantartást és további fejlesztéseket végzünk az oldalon.<br/>
                    <strong>Hamarosan újra jövünk!</strong>
                  </p>
                  <div className="mt-6 text-white/70 text-sm">
                    Köszönjük a türelmét! 🏠
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const AnalyticsTab = () => <AdvancedAnalytics />;

  const LeadsTab = () => <CRMLeadManager />;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-white text-xl">Betöltés...</div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-6xl mb-4">🚫</div>
          <h1 className="text-white text-2xl font-bold mb-2">Hozzáférés megtagadva</h1>
          <p className="text-gray-400 mb-4">Csak Super Admin felhasználók férhetnek hozzá ehhez az oldalhoz.</p>
          <p className="text-gray-500 text-sm">Átirányítás a főoldalra...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <div className="bg-[#1a1a1a] border-b border-secondary/30 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold text-secondary">HoppHomes Admin</h1>
            <div className="flex items-center space-x-2 text-sm text-gray-400">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>Real-time</span>
            </div>
          </div>
          <button
            onClick={() => window.location.href = "/"}
            className="bg-secondary hover:bg-hover text-primary px-4 py-2 rounded-lg font-medium shadow-glow-turquoise transition-colors"
          >
            Kilépés
          </button>
        </div>
      </div>

      {/* Dashboard Grid Navigation */}
      <div className="p-4">
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
          {[
            { 
              id: "overview", 
              title: "Áttekintés", 
              icon: "📊", 
              description: "Rendszer statisztikák",
              color: "from-blue-500 to-blue-600"
            },
            { 
              id: "user-management", 
              title: "🚀 User Management", 
              icon: "👥", 
              description: "Komplett felhasználókezelés",
              color: "from-red-500 to-pink-600",
              isSpecial: true
            },
            { 
              id: "hoppx-ghl-pro", 
              title: "HoppX GHL Pro", 
              icon: "🚀", 
              description: "Teljes marketing platform",
              color: "from-purple-600 to-indigo-700",
              isSpecial: true,
              hasSubmenu: true
            },
            { 
              id: "analytics", 
              title: "Analitika", 
              icon: "📈", 
              description: "Részletes elemzések",
              color: "from-indigo-500 to-indigo-600"
            },
            { 
              id: "mlm", 
              title: "MLM Tracker", 
              icon: "🔗", 
              description: "Hálózati követés",
              color: "from-pink-500 to-rose-500"
            },
            { 
              id: "crm", 
              title: "CRM", 
              icon: "📋", 
              description: "Ügyfélkapcsolatok",
              color: "from-teal-500 to-cyan-500"
            },
            { 
              id: "newsletter", 
              title: "Marketing Auto", 
              icon: "📧", 
              description: "Automatizált marketing",
              color: "from-red-500 to-pink-500"
            },
            { 
              id: "maintenance", 
              title: "Karbantartás", 
              icon: "🔧", 
              description: "Oldal ki/bekapcsolás",
              color: "from-orange-500 to-red-500"
            },
            { 
              id: "notifications", 
              title: "Értesítések", 
              icon: "🔔", 
              description: "Rendszer üzenetek",
              color: "from-amber-500 to-yellow-500"
            },
            { 
              id: "ai-analytics", 
              title: "AI Analitika", 
              icon: "🤖", 
              description: "Mesterséges intelligencia",
              color: "from-violet-500 to-purple-500"
            },
            { 
              id: "payments", 
              title: "Fizetések", 
              icon: "💰", 
              description: "Pénzügyi kezelés",
              color: "from-emerald-500 to-green-500"
            },
            { 
              id: "activity", 
              title: "Aktivitás", 
              icon: "📊", 
              description: "Felhasználói tevékenység",
              color: "from-slate-500 to-gray-600"
            },
            { 
              id: "construction-leads", 
              title: "Építkezési Leadek", 
              icon: "🏗️", 
              description: "Zárt partnermodell",
              color: "from-amber-500 to-orange-600",
              isSpecial: true
            },
            { 
              id: "partners", 
              title: "Partnerek", 
              icon: "🤝", 
              description: "Kivitelező partnerek",
              color: "from-purple-500 to-violet-600",
              isSpecial: true
            }
          ].map(module => (
            <button
              key={module.id}
              onClick={() => {
                if (module.id === 'user-management') {
                  window.location.href = '/admin/user-management';
                } else {
                  setActiveTab(module.id);
                }
              }}
              className={`group relative overflow-hidden rounded-xl p-3 transition-all duration-300 hover:scale-105 hover:shadow-xl ${
                activeTab === module.id 
                  ? "ring-2 ring-[#e31937] shadow-lg" 
                  : "hover:shadow-lg"
              }`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${module.color} opacity-90 group-hover:opacity-100 transition-opacity`}></div>
              <div className="absolute inset-0 bg-black/20"></div>
              <div className="relative z-10 text-center">
                <div className="text-xl mb-1">{module.icon}</div>
                <h3 className={`font-bold text-xs mb-1 ${module.isSpecial ? 'text-yellow-300' : 'text-white'}`}>
                  {module.title}
                </h3>
                <p className="text-white/80 text-xs leading-tight">{module.description}</p>
              </div>
              {activeTab === module.id && (
                <div className="absolute top-1 right-1 w-2 h-2 bg-white rounded-full z-20"></div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === "overview" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Összes felhasználó</p>
                    <p className="text-2xl font-bold text-white">{realStats.totalUsers.toLocaleString()}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">GHL Kontaktok</p>
                    <p className="text-2xl font-bold text-white">{realStats.ghlContacts.toLocaleString()}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Opportunities</p>
                    <p className="text-2xl font-bold text-white">{realStats.opportunities.toLocaleString()}</p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Havi bevétel</p>
                    <p className="text-2xl font-bold text-white">{new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', minimumFractionDigits: 0 }).format(realStats.monthlyRevenue)}</p>
                  </div>
                  <div className="w-12 h-12 bg-[#e31937]/20 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#e31937]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10">
              <h3 className="text-xl font-bold text-white mb-4">Rendszer állapot</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  <span className="text-gray-300">Firebase kapcsolat</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  <span className="text-gray-300">GHL API</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <span className="text-gray-300">Email szolgáltatás</span>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === "hoppx-ghl-pro" && <HoppXGHLPro />}
        {activeTab === "users" && <UsersTab />}
        {activeTab === "analytics" && <AnalyticsTab />}
        {activeTab === "mlm" && <MLMTrackerTab />}
        {activeTab === "crm" && <CRMTab />}
        {activeTab === "newsletter" && <NewsletterTab />}
        {activeTab === "maintenance" && <MaintenanceTab />}
        {activeTab === "notifications" && <NotificationsTab />}
        {activeTab === "ai-analytics" && <AIAnalyticsDashboard />}
        {activeTab === "marketing" && <MarketingAutomation />}
        {activeTab === "payments" && <PaymentCommissionManager />}
        {activeTab === "ai-uploader" && <AIAssetUploader />}
        {activeTab === "ai-assistant" && <AIAssistant />}
        {activeTab === "activity" && <UserActivityDashboard />}
        {activeTab === "construction-leads" && <ConstructionLeadsManagement />}
        {activeTab === "partners" && <PartnerManagement />}
      </div>
    </div>
  );
}

// Helper Components
const StatCard = ({ title, value, icon, color, trend }) => (
  <div className={`${color} rounded-xl p-6 text-white shadow-lg`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-white/80 text-sm font-medium">{title}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
        {trend && <p className="text-white/80 text-sm mt-1">{trend}</p>}
      </div>
      <div className="text-3xl opacity-80">{icon}</div>
    </div>
  </div>
);

const ActionButton = ({ icon, label, color, onClick }) => (
  <button
    onClick={onClick}
    className={`${color} hover:opacity-90 text-white rounded-xl p-4 flex flex-col items-center space-y-2 transition-all hover:scale-105 shadow-lg`}
  >
    <div className="text-2xl">{icon}</div>
    <div className="text-xs font-medium text-center">{label}</div>
  </button>
);
